import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { format } from 'date-fns';
import {
  CREW_ALERT_AUDIENCE_LABELS,
  CREW_ALERT_MAX_LENGTH,
  CREW_ALERT_PRIORITY_LABELS,
  crewAlertRecipients,
  crewAlertsService,
  validateCrewAlert,
  type CrewAlertAudience,
  type CrewAlertPriority,
  type CrewAlertSummary,
  type CrewMember,
} from '../services/crewAlerts';

const PRIORITIES = Object.keys(CREW_ALERT_PRIORITY_LABELS) as CrewAlertPriority[];
const AUDIENCES = Object.keys(CREW_ALERT_AUDIENCE_LABELS) as CrewAlertAudience[];
const PRIORITY_COLOURS: Record<CrewAlertPriority, 'primary' | 'warning' | 'danger'> = {
  info: 'primary',
  important: 'warning',
  urgent: 'danger',
};

/** Office → crew: send an alert to fitters and labourers, and see who has read recent ones. */
export const CrewAlertModal: React.FC<{
  open: boolean;
  onClose: () => void;
  sender: { id: string | null; name: string | null };
}> = ({ open, onClose, sender }) => {
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [recent, setRecent] = useState<CrewAlertSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState<CrewAlertPriority>('info');
  const [audience, setAudience] = useState<CrewAlertAudience>('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const loadRecent = () => crewAlertsService.listRecent().then(setRecent).catch(() => setRecent([]));

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setMessage('');
    setPriority('info');
    setAudience('all');
    setSelectedIds([]);
    setError(null);
    setSent(null);
    setLoading(true);
    Promise.all([crewAlertsService.listCrew(), crewAlertsService.listRecent()])
      .then(([crewRows, recentRows]) => {
        setCrew(crewRows);
        setRecent(recentRows);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the crew list'))
      .finally(() => setLoading(false));
  }, [open]);

  const recipients = useMemo(() => crewAlertRecipients(crew, audience, selectedIds), [crew, audience, selectedIds]);

  const send = async () => {
    const problem = validateCrewAlert({ message, recipients });
    if (problem) {
      setError(problem);
      return;
    }
    setSending(true);
    setError(null);
    try {
      const count = await crewAlertsService.send({ title, message, priority, audience, selectedIds, crew, sender });
      setSent(`Alert sent to ${count} ${count === 1 ? 'person' : 'people'}.`);
      setTitle('');
      setMessage('');
      setSelectedIds([]);
      void loadRecent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the alert');
    } finally {
      setSending(false);
    }
  };

  const toggle = (id: string) => {
    setSelectedIds((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));
  };

  return (
    <Modal open={open} onClose={() => !sending && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 600 }, maxHeight: '90vh', overflow: 'auto' }}>
        <DialogTitle>Send crew alert</DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Stack spacing={1.5}>
              <Typography level="body-sm" textColor="neutral.600">
                Shows in each person’s notification bell and pops up when they next have JARVIS open.
              </Typography>
              <FormControl>
                <FormLabel>Title</FormLabel>
                <Input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Site closed tomorrow" />
              </FormControl>
              <FormControl required>
                <FormLabel>Message</FormLabel>
                <Textarea minRows={3} value={message} onChange={(event) => setMessage(event.target.value)} />
                <FormHelperText>{message.trim().length}/{CREW_ALERT_MAX_LENGTH}</FormHelperText>
              </FormControl>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Priority</FormLabel>
                  <Select value={priority} onChange={(_, value) => value && setPriority(value as CrewAlertPriority)}>
                    {PRIORITIES.map((option) => <Option key={option} value={option}>{CREW_ALERT_PRIORITY_LABELS[option]}</Option>)}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 2 }}>
                  <FormLabel>Send to</FormLabel>
                  <Select value={audience} onChange={(_, value) => value && setAudience(value as CrewAlertAudience)}>
                    {AUDIENCES.map((option) => <Option key={option} value={option}>{CREW_ALERT_AUDIENCE_LABELS[option]}</Option>)}
                  </Select>
                </FormControl>
              </Stack>
              {audience === 'selected' && (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 1.25, maxHeight: 180, overflow: 'auto' }}>
                  <Stack spacing={0.75}>
                    {crew.map((member) => (
                      <Checkbox
                        key={member.id}
                        checked={selectedIds.includes(member.id)}
                        onChange={() => toggle(member.id)}
                        label={`${member.name} · ${member.role === 'labourer' ? 'Labourer' : 'Fitter'}`}
                      />
                    ))}
                    {crew.length === 0 && <Typography level="body-sm">No active fitters or labourers.</Typography>}
                  </Stack>
                </Sheet>
              )}
              <Typography level="body-xs" textColor="neutral.600">
                {recipients.length === 0
                  ? 'Nobody selected yet.'
                  : `Goes to ${recipients.length}: ${recipients.map((member) => member.name).join(', ')}`}
              </Typography>
              {error && <Alert color="danger">{error}</Alert>}
              {sent && <Alert color="success">{sent}</Alert>}

              {recent.length > 0 && (
                <>
                  <Divider />
                  <Typography level="title-sm">Recent alerts</Typography>
                  <Stack spacing={1}>
                    {recent.map((alert) => (
                      <Sheet key={alert.alertId} variant="soft" sx={{ borderRadius: 'sm', p: 1 }}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                            <Typography level="body-sm" sx={{ wordBreak: 'break-word' }}>{alert.message}</Typography>
                            <Typography level="body-xs" textColor="neutral.500">
                              {format(new Date(alert.createdAt), 'd MMM yyyy, HH:mm')}
                              {alert.senderName ? ` · ${alert.senderName}` : ''}
                              {alert.unreadNames.length > 0 ? ` · Not read: ${alert.unreadNames.join(', ')}` : ''}
                            </Typography>
                          </Stack>
                          <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                            <Chip size="sm" variant="soft" color={PRIORITY_COLOURS[alert.priority]}>
                              {CREW_ALERT_PRIORITY_LABELS[alert.priority]}
                            </Chip>
                            <Typography level="body-xs">{alert.read} of {alert.recipients} read</Typography>
                          </Stack>
                        </Stack>
                      </Sheet>
                    ))}
                  </Stack>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={send} loading={sending} disabled={loading} color={priority === 'urgent' ? 'danger' : 'primary'}>
            Send alert
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={sending}>Close</Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default CrewAlertModal;
